import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award,
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Loader2,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Sidebar from '../components/layout/Sidebar';
import {
  achievementService,
  type AchievementType,
  type AdminAchievementListItem,
  type AdminAchievementSortBy,
  type ListAdminAchievementsParams,
} from '../services/achievementService';
import { schoolService } from '../services/schoolService';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// Visual metadata for each achievement type. Mirrored from the Student
// add-achievement chip picker so the same Award shows the same color
// here as it did when the student typed it in. `label` is hard-coded
// since this is an admin tool — i18n parity with the student app
// isn't a goal.
const TYPE_OPTIONS: ReadonlyArray<{ value: AchievementType; label: string; chipClass: string }> = [
  { value: 'AWARD', label: 'Award', chipClass: 'bg-amber-100 text-amber-800' },
  { value: 'CERTIFICATION', label: 'Certification', chipClass: 'bg-blue-100 text-blue-800' },
  { value: 'COURSE', label: 'Course', chipClass: 'bg-indigo-100 text-indigo-800' },
  { value: 'PROJECT', label: 'Project', chipClass: 'bg-purple-100 text-purple-800' },
  { value: 'JOB', label: 'Job', chipClass: 'bg-slate-100 text-slate-800' },
  { value: 'VOLUNTEER', label: 'Volunteer', chipClass: 'bg-rose-100 text-rose-800' },
  { value: 'LEADERSHIP', label: 'Leadership', chipClass: 'bg-cyan-100 text-cyan-800' },
  { value: 'OTHER', label: 'Other', chipClass: 'bg-gray-100 text-gray-700' },
];

const TYPE_CHIP_CLASS = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.chipClass]));
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label]));

// Table columns. `sortKey` is set for the seven columns the server can
// sort across the whole result set; the Verified column has none (it's
// derived from a separate query, so it isn't sortable). `align` drives
// the header/cell justification for the centered Verified column.
const COLUMNS: ReadonlyArray<{
  label: string;
  sortKey?: AdminAchievementSortBy;
  align?: 'center';
}> = [
  { label: 'Student', sortKey: 'student' },
  { label: 'School', sortKey: 'school' },
  { label: 'Type', sortKey: 'type' },
  { label: 'Title', sortKey: 'title' },
  { label: 'Organization', sortKey: 'organization' },
  { label: 'Linked award', sortKey: 'linkedAward' },
  { label: 'Added', sortKey: 'createdAt' },
  { label: 'Verified', align: 'center' },
];

interface SchoolPickerOption {
  id: string;
  name: string;
}

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fullName = (first: string | null, last: string | null, email: string): string => {
  const trimmed = [first, last].filter(Boolean).join(' ').trim();
  return trimmed || email;
};

/**
 * Platform-Admin "Manage Achievements" — browseable list of every
 * achievement across the platform, with server-side filters for type,
 * organization, school, free-text search, and a creation-date range.
 * Powered by GET /v1/dashboard/admins/achievements (admin-gated).
 *
 * Filters debounce 300ms so typing isn't blocked by a request burst.
 * Every filter change snaps page back to 1 — otherwise the operator
 * narrows a filter and lands on a stale page that may now be empty.
 */
export default function ManageAchievements() {
  // Filter state. `typeFilter` is "ALL" when unset so the dropdown has a
  // defined value (vs undefined, which React treats as uncontrolled).
  const [typeFilter, setTypeFilter] = useState<AchievementType | 'ALL'>('ALL');
  const [orgFilter, setOrgFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState<string>('ALL');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Linked-to-Award-catalog filter. 'ALL' = no filter (default); the
  // 'linked'/'unlinked' values translate directly to the server's
  // `linked` query param. Used for data-quality audits ("show me all
  // the Loran entries someone typed freehand") and adoption tracking
  // ("how many wins are linked at all").
  const [linkedFilter, setLinkedFilter] = useState<'ALL' | 'linked' | 'unlinked'>('ALL');
  // Column sort. Defaults to the server's own default (newest first) so
  // the initial render matches what an unsorted request returns.
  const [sortBy, setSortBy] = useState<AdminAchievementSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Data state.
  const [rows, setRows] = useState<AdminAchievementListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolPickerOption[]>([]);

  // Out-of-order guard for the fetch — a slow response from filter set A
  // can't overwrite the fresh response for filter set B.
  const fetchSeqRef = useRef(0);

  // Debounce typed inputs (free-text q + organization) into the actual
  // request triggers. Text changes don't immediately fire a network call.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput]);

  const [orgQuery, setOrgQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setOrgQuery(orgFilter.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [orgFilter]);

  // Snap to page 1 when any non-debounced filter changes (date range,
  // type, school) — otherwise the operator narrows and ends up on a
  // page that may now be out of bounds.
  useEffect(() => {
    setPage(1);
  }, [typeFilter, schoolFilter, startDate, endDate, linkedFilter, sortBy, sortDir]);

  // Clicking a header sorts by that column. Re-clicking the active column
  // flips direction; switching columns starts at the natural default —
  // newest-first for the date column, A→Z for everything else.
  const toggleSort = useCallback(
    (key: AdminAchievementSortBy) => {
      if (sortBy === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortDir(key === 'createdAt' ? 'desc' : 'asc');
      }
    },
    [sortBy],
  );

  // ── Load school list for the dropdown (active schools only — they're
  //    the ones whose students have meaningful achievement counts).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await schoolService.getSchools({ hasAdmin: true, limit: 200, page: 1 });
        if (cancelled) return;
        setSchools((response.data ?? []).map((s) => ({ id: s.id, name: s.name })));
      } catch {
        // Non-fatal — operator can still use other filters.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Main fetch ────────────────────────────────────────────────────────
  const fetchAchievements = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setIsLoading(true);
    try {
      const params: ListAdminAchievementsParams = {
        page,
        pageSize: PAGE_SIZE,
      };
      if (typeFilter !== 'ALL') params.type = typeFilter;
      if (orgQuery) params.organization = orgQuery;
      if (schoolFilter !== 'ALL') params.schoolId = schoolFilter;
      if (q) params.q = q;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (linkedFilter !== 'ALL') params.linked = linkedFilter;
      params.sortBy = sortBy;
      params.sortDir = sortDir;

      const response = await achievementService.listAdminAchievements(params);
      if (fetchSeqRef.current !== seq) return;
      setRows(response.data);
      setTotal(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (err: unknown) {
      if (fetchSeqRef.current !== seq) return;
      const message = err instanceof Error ? err.message : 'Failed to load achievements';
      toast.error(message);
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [page, typeFilter, orgQuery, schoolFilter, q, startDate, endDate, linkedFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  const clearAllFilters = () => {
    setTypeFilter('ALL');
    setOrgFilter('');
    setSchoolFilter('ALL');
    setQInput('');
    setStartDate('');
    setEndDate('');
    setLinkedFilter('ALL');
    setPage(1);
  };

  const hasAnyFilter = useMemo(
    () =>
      typeFilter !== 'ALL' ||
      schoolFilter !== 'ALL' ||
      orgFilter.trim().length > 0 ||
      qInput.trim().length > 0 ||
      startDate.length > 0 ||
      endDate.length > 0 ||
      linkedFilter !== 'ALL',
    [typeFilter, schoolFilter, orgFilter, qInput, startDate, endDate, linkedFilter],
  );

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Sidebar />

      <div className="ml-[230px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-gray-900 text-2xl font-bold">Manage Achievements</h2>
            <p className="text-gray-500 text-base font-normal">
              Browse achievements across every student. Filter by type, organization, or school to audit
              entries.
            </p>
          </div>
        </div>

        {/* ── Filters ──────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Type */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Type
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as AchievementType | 'ALL')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All types</option>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            {/* School */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              School
              <select
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All schools</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            {/* Organization substring */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Organization contains
              <input
                type="text"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                placeholder="e.g., Hudson College"
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>

            {/* Free-text search */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Search title or organization
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Type to search"
                  className="w-full pl-8 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </label>

            {/* Date range — filters on createdAt. The two inputs are
                independent so the operator can leave one open-ended. */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Added on or after
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Added on or before
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>

            {/* Linked-to-Award-catalog filter. Audit shortcut: pair
                with the organization filter to spot freehand "Loran"
                entries that should have been picker-linked. */}
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              Linked to known award
              <select
                value={linkedFilter}
                onChange={(e) => setLinkedFilter(e.target.value as 'ALL' | 'linked' | 'unlinked')}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All</option>
                <option value="linked">Linked only</option>
                <option value="unlinked">Unlinked only</option>
              </select>
            </label>
          </div>

          {hasAnyFilter && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {isLoading ? 'Loading…' : `${total.toLocaleString()} matching achievement${total === 1 ? '' : 's'}`}
              </span>
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                <X size={12} /> Clear filters
              </button>
            </div>
          )}
        </div>

        {/* ── Table ────────────────────────────────────────────────────── */}
        <div className="border border-blue-100 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-50 text-gray-700">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide">
                  {COLUMNS.map((col) => {
                    const isActive = col.sortKey && sortBy === col.sortKey;
                    const centered = col.align === 'center';
                    if (!col.sortKey) {
                      return (
                        <th key={col.label} className={`px-6 py-3 ${centered ? 'text-center' : ''}`}>
                          {col.label}
                        </th>
                      );
                    }
                    return (
                      <th key={col.label} className={`px-6 py-3 ${centered ? 'text-center' : ''}`}>
                        <button
                          type="button"
                          onClick={() => toggleSort(col.sortKey!)}
                          aria-label={`Sort by ${col.label}`}
                          aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className={`group inline-flex items-center gap-1 uppercase tracking-wide hover:text-blue-700 transition-colors ${
                            isActive ? 'text-blue-700' : ''
                          } ${centered ? 'justify-center' : ''}`}
                        >
                          {col.label}
                          {isActive ? (
                            sortDir === 'asc' ? (
                              <ChevronUp size={12} className="shrink-0" />
                            ) : (
                              <ChevronDown size={12} className="shrink-0" />
                            )
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              className="shrink-0 text-gray-300 group-hover:text-blue-400"
                            />
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <Loader2 className="inline-block w-5 h-5 animate-spin text-gray-400" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      <Award className="inline-block w-6 h-6 text-gray-300 mb-2" />
                      <div className="text-sm">
                        {hasAnyFilter
                          ? 'No achievements match these filters.'
                          : 'No achievements have been logged yet.'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-xs text-gray-900">
                        <div className="font-medium">{fullName(r.studentFirstName, r.studentLastName, r.studentEmail)}</div>
                        <div className="text-gray-400">{r.studentEmail}</div>
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-900">{r.schoolName ?? '—'}</td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            TYPE_CHIP_CLASS[r.achievementType] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {TYPE_LABEL[r.achievementType] ?? r.achievementType}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-900 max-w-xs">
                        <div className="truncate" title={r.title}>{r.title}</div>
                        {r.categoryName && <div className="text-gray-400 truncate">{r.categoryName}</div>}
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-900 max-w-xs truncate" title={r.organization}>
                        {r.organization}
                      </td>
                      <td className="px-6 py-3 text-xs max-w-xs">
                        {r.linkedAward ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium"
                            title={`Linked to ${r.linkedAward.name}${r.linkedAward.sponsor ? ` · ${r.linkedAward.sponsor}` : ''}`}
                          >
                            <Sparkles className="w-3 h-3" />
                            <span className="truncate max-w-[140px]">{r.linkedAward.name}</span>
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-900 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      <td className="px-6 py-3 text-center">
                        {r.verified ? (
                          <BadgeCheck className="inline-block w-4 h-4 text-emerald-600" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">
            {total === 0
              ? 'No results'
              : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-700 px-3">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoading}
              className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
